/**
 * Knowledge Archive filter panel.
 *
 * Composer for the per-filter sub-components - each one owns its own
 * piece of state via `useTagFilters`, so adding a new filter type is a
 * matter of dropping its component into this panel.
 *
 * Search lives in the page header (above the graph), not here - it's
 * a "find a specific concept" interaction rather than a "narrow the
 * set" one, even though it shares the same `?q=` URL param.
 *
 * Composition (top → bottom):
 *
 *     [ Active filter chips  ]   ← only when something is active;
 *                                  preserves the user's view of what's on
 *                                  even when every section is collapsed.
 *     [ Source lessons   ▾ ]
 *     [ Domain tags      ▾ ]
 *     [ Your tags        ▾ ]
 *     [ Time window      ▾ ]
 *     [ Clear N filters ]        ← sticky footer; count in label
 */

import { X } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { useKnowledgeArchiveGraph } from '@/hooks/mindspace/useMindspace';
import {
  countActiveFilters,
  useTagFilters,
} from '@/hooks/mindspace/useTagFilters';
import { SourceLessonFilter } from '@/components/features/mindspace/filters/SourceLessonFilter';
import { DomainTagFilter } from '@/components/features/mindspace/filters/DomainTagFilter';
import { UserTagFilter } from '@/components/features/mindspace/filters/UserTagFilter';
import { TimeWindowFilter } from '@/components/features/mindspace/filters/TimeWindowFilter';
import { ActiveFilterChips } from '@/components/features/mindspace/filters/ActiveFilterChips';
import {
  FilterLayoutContext,
  type FilterLayout,
} from '@/components/features/mindspace/filters/filterLayout';

interface MindspaceFilterPanelProps {
  /** `'sheet'` - the phone's: every section open, quickest first, no chip
   *  strip (the page header shows the chips) and no Clear footer (the
   *  sheet's own footer has it). See `filterLayout`. */
  layout?: FilterLayout;
}

export function MindspaceFilterPanel({
  layout = 'panel',
}: MindspaceFilterPanelProps) {
  const filterState = useTagFilters();
  // Pull the current graph payload (same filters as the page) so the
  // domain/user tag filters can offer in-context options. This shares
  // React Query cache with the page - no double fetch.
  const { data } = useKnowledgeArchiveGraph(filterState.asGraphFilters());

  const clearAll = filterState.clearAll;

  const activeCount = countActiveFilters(filterState);

  if (layout === 'sheet') {
    return (
      <FilterLayoutContext.Provider value='sheet'>
        <TimeWindowFilter showPausedToggle />
        <DomainTagFilter nodes={data?.nodes} />
        <UserTagFilter nodes={data?.nodes} />
        <SourceLessonFilter />
      </FilterLayoutContext.Provider>
    );
  }

  return (
    <div className='flex h-full flex-col'>
      <ActiveFilterChips showPausedChip />
      <div className='min-h-0 flex-1 overflow-y-auto py-1'>
        <SourceLessonFilter />
        <DomainTagFilter nodes={data?.nodes} />
        <UserTagFilter nodes={data?.nodes} />
        <TimeWindowFilter showPausedToggle />
      </div>
      {filterState.hasAnyFilter && (
        <div className='flex-shrink-0 p-3'>
          <Button
            variant='outline'
            size='sm'
            onClick={clearAll}
            className='w-full gap-1.5'
          >
            <X className='h-3 w-3' />
            Clear {activeCount} filter{activeCount === 1 ? '' : 's'}
          </Button>
        </div>
      )}
    </div>
  );
}
